import React, { useState, useEffect } from 'react';
import { CheckSquare, Plus, Trash2, Check } from 'lucide-react';
import ActivityLayout, {
    Toggle, SettingsGroup, SettingsRow, SubRow,
    InlineInput, InlineTextarea, AIButton, AIUndoButton, AIOptionButton, PollResults, AddImageSection, QuestionImage,
} from './ActivityLayout';
import Tooltip from '../../components/ui/Tooltip';
import styles from './ActivityLayout.module.css';
import useActivityContent from '../../hooks/useActivityContent';
import ParticipantExperience from '../join/ParticipantExperience';
import { API_PATH, request } from '../../config/api';
import { useConfirm } from '../../store/ConfirmDialogContext';

const DUMMY_DATA = {
    question: 'Which planet is known as the Red Planet? 🔴',
    options: ['Earth', 'Mars', 'Jupiter', 'Saturn'],
    settings: { multipleOptions: false, maxSelect: 2, markCorrect: true, description: true, descText: 'Choose the correct answer from the options below.', pollResults: 'hidden', showCorrectAnswer: false, isLocked: false, allowUpdateResponse: true, imageUrl: null },
};

const MultipleChoice = ({
    event, activity, isLaunched, onLaunch, onStop,
    onNext, onPrev, hasNext, hasPrev, onSettingsUpdated
}) => {
    const confirm = useConfirm();
    const [question, setQuestion] = useState('');
    const [editingQuestion, setEditingQuestion] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [options, setOptions] = useState(['Option A', 'Option B']);
    const [correctOptions, setCorrectOptions] = useState(new Set());
    const [settings, setSettings] = useState({
        multipleOptions: false,
        maxSelect: 2,
        markCorrect: false,
        description: false,
        descText: '',
        pollResults: 'hidden',
        showCorrectAnswer: false,
        isLocked: false,
        allowUpdateResponse: true,
        imageUrl: null,
    });

    const { content, isLoading, isSaving, saveStatus, saveContent } =
        useActivityContent(event?.id, activity?.id);

    const [isImproving, setIsImproving] = useState(false);
    const [isImprovingDesc, setIsImprovingDesc] = useState(false);
    const [isAddingOptions, setIsAddingOptions] = useState(false);
    const [previousQuestion, setPreviousQuestion] = useState(null);
    const [previousDescText, setPreviousDescText] = useState(null);
    const API_BASE = API_PATH;

    const handleImproveWithAI = async () => {
        if (!question.trim()) return;
        setIsImproving(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text: question }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousQuestion(question);
                    setQuestion(data.suggestion);
                }
            } else {
                alert('Failed to improve with AI. Please try again.');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to improve with AI. Please try again.');
        } finally {
            setIsImproving(false);
        }
    };

    const handleUndoQuestion = () => {
        if (previousQuestion !== null) {
            setQuestion(previousQuestion);
            setPreviousQuestion(null);
        }
    };

    const handleImproveDescWithAI = async () => {
        const text = settings.descText?.trim() || '';
        if (!text) return;
        setIsImprovingDesc(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousDescText(text);
                    set('descText', data.suggestion);
                }
            } else {
                alert('Failed to improve with AI. Please try again.');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to improve with AI. Please try again.');
        } finally {
            setIsImprovingDesc(false);
        }
    };

    const handleUndoDesc = () => {
        if (previousDescText !== null) {
            set('descText', previousDescText);
            setPreviousDescText(null);
        }
    };

    const handleAddOptionsWithAI = async () => {
        setIsAddingOptions(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'options', context: { question } }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestions) {
                    setOptions(prev => [...prev, ...data.suggestions]);
                }
            } else {
                alert('Failed to generate options. Please try again.');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to generate options. Please try again.');
        } finally {
            setIsAddingOptions(false);
        }
    };

    useEffect(() => {
        if (!content || Object.keys(content).length === 0) return;
        if (content.question !== undefined) setQuestion(content.question);
        if (content.options) setOptions(content.options);
        if (content.correctOptions) setCorrectOptions(new Set(content.correctOptions));
        if (content.settings) setSettings(s => ({ ...s, ...content.settings }));
    }, [content]);

    const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
    const addOption = () => setOptions(prev => [...prev, '']);
    const updateOption = (i, v) => setOptions(prev => prev.map((o, j) => (j === i ? v : o)));

    const removeOption = async (i) => {
        const responseCount = activity?.response_count || 0;
        const ok = await confirm({
            title: 'Delete Option',
            message: 'Delete this option?',
            // Responses are stored against the option's text/id at the time they were
            // submitted, not against its position — removing it doesn't delete those
            // responses, but they'll no longer match a visible option in results. Same
            // warn-don't-silently-corrupt approach as deleting a whole poll.
            details: responseCount > 0 ? (
                <>
                    <span style={{ fontWeight: 600 }}>⚠️ This poll already has {responseCount} response{responseCount !== 1 ? 's' : ''}.</span>
                    <br />
                    If any of them chose this option, it will no longer appear in the results breakdown.
                </>
            ) : undefined,
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setOptions(prev => prev.filter((_, j) => j !== i));
        setCorrectOptions(prev => {
            if (prev.size === 0) return prev;
            const next = new Set();
            prev.forEach((idx) => {
                if (idx === i) return;
                next.add(idx > i ? idx - 1 : idx);
            });
            return next;
        });
    };

    const toggleCorrect = (i) => {
        setCorrectOptions(prev => {
            const next = new Set(prev);
            if (next.has(i)) {
                next.delete(i);
                return next;
            }
            if (!settings.multipleOptions) {
                next.clear();
            }
            next.add(i);
            return next;
        });
    };

    const openQuestionEditor = () => setEditingQuestion(true);
    const closeQuestionEditor = () => setEditingQuestion(false);
    const openDescEditor = () => setEditingDesc(true);
    const closeDescEditor = () => setEditingDesc(false);

    const handleKeyToEdit = (e, openFn) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openFn();
        }
    };

    const handleEditKeyDown = (e, closeFn) => {
        if (e.key === 'Enter' || e.key === 'Escape') {
            e.preventDefault();
            closeFn();
        }
    };

    const right = (
        <>
            <SettingsGroup title="Poll Settings">
                <SettingsRow label="Multiple options" description="Participants can select more than one option">
                    <Toggle
                        checked={settings.multipleOptions}
                        onChange={(e) => {
                            const on = e.target.checked;
                            set('multipleOptions', on);
                            if (on) set('markCorrect', false);
                        }}
                    />
                </SettingsRow>
                {settings.multipleOptions && (
                    <SubRow>
                        <span>Participants can select up to</span>
                        <InlineInput
                            type="number"
                            value={settings.maxSelect}
                            onChange={(e) => set('maxSelect', Number(e.target.value))}
                            min={2}
                            max={options.length}
                        />
                    </SubRow>
                )}

                <SettingsRow
                    label="Mark correct answer"
                    description={settings.multipleOptions ? 'Not available when multiple options is on' : 'Select and show the correct answer to participants'}
                >
                    <Toggle
                        checked={settings.markCorrect}
                        onChange={(e) => set('markCorrect', e.target.checked)}
                        disabled={settings.multipleOptions}
                    />
                </SettingsRow>

                <SettingsRow label="Poll description" description="Add description visible to participants">
                    <Toggle checked={settings.description} onChange={(e) => set('description', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup title="Poll Results">
                <PollResults value={settings.pollResults} onChange={(v) => set('pollResults', v)} />
                <SettingsRow
                    label="Allow participants to update response"
                    description="Let participants change their answer after submitting"
                >
                    <Toggle checked={settings.allowUpdateResponse !== false} onChange={(e) => set('allowUpdateResponse', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup>
                <AddImageSection
                    imageUrl={settings.imageUrl}
                    onImageChange={(url) => set('imageUrl', url)}
                />
            </SettingsGroup>
        </>
    );

    const left = (
        <>
            <div className={`${styles.questionCard} ${settings.imageUrl ? styles.questionCardWithImage : ''}`}>
                {settings.imageUrl && (
                    <QuestionImage imageUrl={settings.imageUrl} onImageChange={(url) => set('imageUrl', url)} />
                )}
                <div className={styles.questionContent}>

                    {/* ── Question field ── */}
                    <div className={styles.questionLabelRow}>
                        <span className={styles.questionLabel}>
                            Question <span className={styles.requiredStar}>*</span>
                        </span>
                        <span className={styles.aiActions}>
                            <AIButton onClick={handleImproveWithAI} loading={isImproving} />
                            {previousQuestion !== null && (
                                <AIUndoButton onClick={handleUndoQuestion} label="Undo" />
                            )}
                        </span>
                    </div>
                    {editingQuestion ? (
                        <input
                            className={styles.questionInput}
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            placeholder="Type your question here..."
                            autoFocus
                            onBlur={closeQuestionEditor}
                            onKeyDown={(e) => handleEditKeyDown(e, closeQuestionEditor)}
                        />
                    ) : (
                        <Tooltip title="Click to edit question" placement="top">
                            <div
                                className={`${styles.questionTextDisplay} ${!question ? styles.questionTextPlaceholder : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={openQuestionEditor}
                                onKeyDown={(e) => handleKeyToEdit(e, openQuestionEditor)}
                            >
                                {question || 'Type your question here...'}
                            </div>
                        </Tooltip>
                    )}

                    {/* ── Optional description ── */}
                    {settings.description && (
                        <div className={styles.questionDescription}>
                            <div className={styles.questionLabelRow}>
                                <span className={styles.descLabel}>Description</span>
                                <span className={styles.aiActions}>
                                    <AIButton onClick={handleImproveDescWithAI} loading={isImprovingDesc} />
                                    {previousDescText !== null && (
                                        <AIUndoButton onClick={handleUndoDesc} label="Undo" />
                                    )}
                                </span>
                            </div>
                            {editingDesc ? (
                                <input
                                    className={styles.descInput}
                                    value={settings.descText}
                                    onChange={(e) => set('descText', e.target.value)}
                                    placeholder="Type description here..."
                                    autoFocus
                                    onBlur={closeDescEditor}
                                    onKeyDown={(e) => handleEditKeyDown(e, closeDescEditor)}
                                />
                            ) : (
                                <Tooltip title="Click to edit description" placement="top">
                                    <div
                                        className={`${styles.descTextDisplay} ${!settings.descText ? styles.descTextPlaceholder : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={openDescEditor}
                                        onKeyDown={(e) => handleKeyToEdit(e, openDescEditor)}
                                    >
                                        {settings.descText || 'Type description here...'}
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    )}

                    {/* ── Options ── */}
                    <div className={styles.optionsCard}>
                        <div className={styles.optionsHeader}>
                            <span className={styles.optionsLabel}>Options</span>
                            {settings.markCorrect && (
                                <span className={styles.markCorrectHint}>
                                    Click the badge to mark correct
                                </span>
                            )}
                        </div>

                        {options.map((opt, i) => {
                            const isCorrect = correctOptions.has(i);
                            const letter = String.fromCharCode(65 + i);
                            return (
                                <div
                                    key={i}
                                    className={`${styles.optionRow} ${settings.markCorrect && isCorrect ? styles.optionRowCorrect : ''}`}
                                >
                                    {settings.markCorrect ? (
                                        <Tooltip title={isCorrect ? 'Unmark correct' : 'Mark as correct'} placement="top">
                                            <button
                                                className={`${styles.optionLetterBtn} ${isCorrect ? styles.optionLetterBtnCorrect : ''} ${settings.multipleOptions ? styles.optionLetterBtnSquare : ''}`}
                                                onClick={() => toggleCorrect(i)}
                                                aria-label={isCorrect ? `Unmark ${letter} as correct` : `Mark ${letter} as correct`}
                                            >
                                                {isCorrect ? <Check size={11} /> : letter}
                                            </button>
                                        </Tooltip>
                                    ) : (
                                        <span
                                            className={`${styles.optionLetter} ${settings.multipleOptions ? styles.optionLetterSquare : ''}`}
                                        >
                                            {letter}
                                        </span>
                                    )}

                                    <input
                                        className={`${styles.optionInput} ${isCorrect && settings.markCorrect ? styles.optionInputCorrect : ''}`}
                                        value={opt}
                                        onChange={(e) => updateOption(i, e.target.value)}
                                        placeholder={`Option ${letter}`}
                                    />

                                    {settings.markCorrect && isCorrect && (
                                        <span className={styles.correctBadge}>
                                            <Check size={10} /> Correct
                                        </span>
                                    )}

                                    {options.length > 2 && (
                                        <button className={styles.optionRemoveBtn} onClick={() => removeOption(i)} aria-label="Remove option">
                                            <Trash2 size={13} />
                                        </button>
                                    )}
                                </div>
                            );
                        })}

                        <div className={styles.optionsFooter}>
                            <button className={styles.addOptionBtn} onClick={addOption}>
                                <Plus size={14} /> Add option
                            </button>
                            <AIOptionButton onClick={handleAddOptionsWithAI} loading={isAddingOptions} />
                        </div>
                    </div>

                </div>
            </div>
        </>
    );

    const loadDummy = () => {
        setQuestion(DUMMY_DATA.question);
        setOptions(DUMMY_DATA.options);
        setCorrectOptions(new Set([1]));
        setSettings(s => ({ ...s, ...DUMMY_DATA.settings }));
    };

    const participantPreview = (
        <ParticipantExperience
            step="activity"
            name="Preview User"
            event={event}
            activity={{
                type: 'multiple-choice',
                title: question || 'Multiple Choice',
                content: {
                    question,
                    options,
                    settings,
                    correctOptions: [...correctOptions]
                }
            }}
        />
    );

    return (
        <ActivityLayout
            icon={<CheckSquare size={20} />}
            color="var(--cat-violet)"
            title="Multiple Choice"
            eventName={event?.name}
            event={event}
            activity={activity}
            left={left}
            right={right}
            participantPreview={participantPreview}
            validate={() => {
                if (!question.trim()) return 'Question is required.';
                if (options.filter(o => o.trim()).length < 2) return 'At least 2 non-empty options are required.';
                return null;
            }}
            onSave={() => {
                setPreviousQuestion(null);
                setPreviousDescText(null);

                // Strip blank options and remap correctOptions indices
                const indexed = options.map((opt, i) => ({ opt, i })).filter(({ opt }) => opt.trim());
                const cleanOptions = indexed.map(({ opt }) => opt);
                const oldToNew = new Map(indexed.map(({ i }, newIdx) => [i, newIdx]));
                const cleanCorrect = new Set();
                correctOptions.forEach(idx => { if (oldToNew.has(idx)) cleanCorrect.add(oldToNew.get(idx)); });

                setOptions(cleanOptions);
                setCorrectOptions(cleanCorrect);
                saveContent({
                    question,
                    options: cleanOptions,
                    correctOptions: [...cleanCorrect],
                    settings,
                });
            }}
            onLoadExample={loadDummy}
            isSaving={isSaving}
            saveStatus={saveStatus}
            isLoading={isLoading}
            isLaunched={isLaunched}
            onLaunch={onLaunch}
            onStop={onStop}
            onNext={onNext}
            onPrev={onPrev}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onSettingsUpdated={onSettingsUpdated}
        />
    );
};

export default MultipleChoice;
